/**
 * DispositionReasonModal — collects the REQUIRED reason when a recruiter parks a
 * lead at a terminal-negative disposition (Not Interested / Unreachable / Screen
 * Failed / Not Eligible). The reason rides the PATCH /contacts call as
 * { reason_code, disposition_note } and is recorded in the crm.contact_dispositions
 * ledger for the Dispositions report. A controlled reason code is required; the
 * note is optional. Cancelling leaves the lead's status unchanged.
 *
 * Screen Failed is also a STUDY-ATTRIBUTED outcome (the by-study screen-fail
 * numbers drive spend decisions), so with `askStudy` the modal additionally
 * requires the study the screen-fail belongs to; the caller sends it as
 * primary_study_id in the same PATCH (backend: 'study_confirmation_required').
 * In bulk mode (bulkCount) no per-lead pick is possible — the modal states that
 * each lead keeps its own considering study and the caller sends
 * study_confirmed:true.
 */
import { useEffect, useState } from 'react';
import { dispositionReasonOptions, lifecycleMeta } from '../lib';
import StudyPickField from './StudyPickField';

interface Props {
  /** The terminal status being moved to (drives the reason list + title). */
  status: string;
  /** Require a study confirmation too (study-attributed statuses, single lead). */
  askStudy?: boolean;
  /** The lead's current considering study — preselects the study pick. */
  defaultStudyId?: string | null;
  /** Bulk path: number of selected leads (study pick becomes an acknowledgement). */
  bulkCount?: number;
  onConfirm: (reasonCode: string, note: string, studyId?: string) => void;
  onCancel: () => void;
}

export default function DispositionReasonModal({ status, askStudy = false, defaultStudyId, bulkCount, onConfirm, onCancel }: Props) {
  const options = dispositionReasonOptions(status);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [studyId, setStudyId] = useState(defaultStudyId || '');
  const label = lifecycleMeta(status).label;
  const bulk = typeof bulkCount === 'number' && bulkCount > 0;
  const needsStudyPick = askStudy && !bulk;

  // Escape cancels; the dialog traps focus on the select for fast keyboard use.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const confirm = () => {
    if (!reasonCode) return;
    if (needsStudyPick && !studyId) return;
    onConfirm(reasonCode, note.trim(), needsStudyPick ? studyId : undefined);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Reason for ${label}`}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Mark as “{label}”
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Pick the reason this lead is being closed out — it feeds the recruiter
          disposition report.
        </p>

        <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Reason <span className="text-rose-500">*</span>
        </label>
        <select
          autoFocus
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          <option value="" disabled>Select a reason…</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {needsStudyPick && (
          <StudyPickField value={studyId} onChange={setStudyId} />
        )}
        {askStudy && bulk && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            Each of the {bulkCount} leads is recorded under its own considering
            study. If any pivoted to a different study, verify those leads
            individually so the by-study reports stay accurate.
          </p>
        )}

        <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Note <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 1000))}
          rows={3}
          placeholder="Any specifics worth keeping…"
          className="mt-1 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!reasonCode || (needsStudyPick && !studyId)}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save status
          </button>
        </div>
      </div>
    </div>
  );
}
